/*!
 * Copyright (c) REWE Digital GmbH
 */
import { MDCRipple } from "@material/ripple";
import { Host, h, Build } from "@stencil/core";
/**
 * VxButton component.
 *
 * This component is used to render a button with various properties.
 *
 * @example
 * <vx-button label="Click me"></vx-button>
 *
 * @part button - The main button element.
 */
// eslint-disable-next-line require-jsdoc
export class VxButton {
    constructor() {
        /**
         * Handles the click event on the button.
         * If the button type is 'submit', it dispatches a 'submit' event on the closest form.
         */
        this.handleClick = () => {
            var _a;
            if (this.type === 'submit') {
                (_a = this.host.closest('form')) === null || _a === void 0 ? void 0 : _a.dispatchEvent(new Event('submit'));
            }
        };
        this.disabled = false;
        this.ariaLabel = undefined;
        this.raised = false;
        this.fullwidth = false;
        this.icon = undefined;
        this.showLoadingIndicator = false;
        this.unelevated = false;
        this.label = undefined;
        this.success = false;
        this.type = undefined;
        this.background = undefined;
        this.isQuadratic = false;
        this.height = '36px';
        this.width = undefined;
    }
    /**
     * Lifecycle hook that is called after the component loads.
     * Initializes the MDCRipple on the button.
     */
    async componentDidLoad() {
        if (Build.isBrowser) {
            new MDCRipple(this.buttonElement);
        }
    }
    /**
     * Lifecycle hook that is called after the component renders.
     * Sets the height and width of the button.
     */
    componentDidRender() {
        const buttonStyle = this.buttonElement.style;
        buttonStyle.minHeight = this.height;
        buttonStyle.height = this.height;
        if (this.width && !this.fullwidth) {
            buttonStyle.width = this.width;
            buttonStyle.minWidth = this.width;
        }
    }
    /**
     * Watcher for the success prop.
     * Updates the background color of the button when the success state changes.
     */
    handleSuccess(success) {
        let successColor = getComputedStyle(document.documentElement)
            .getPropertyValue('--mdc-theme-success');
        successColor = successColor.length > 0 ? successColor : 'green';
        if (this.disabled) {
            this.background = undefined;
            this.success = false;
        }
        else {
            this.background = success
                ? successColor
                : getComputedStyle(this.host).getPropertyValue('--mdc-theme-primary');
        }
    }
    /**
     * Renders the VxButton component.
     * @returns {JSX.Element} The rendered VxButton component.
     */
    render() {
        var _a;
        return (h(Host, { key: '389269d44d0adb6d91a6479abd5affaa9cec3a70' }, h("button", { key: 'd12d100ca94658bdf868ac7edf5e8fee11a5334a', ref: el => (this.buttonElement = el), type: this.type, style: { backgroundColor: this.background }, class: {
                'mdc-button': true,
                'mdc-button--raised': this.raised,
                fullwidth: this.fullwidth,
            }, onClick: this.handleClick, disabled: this.disabled, "aria-disabled": this.disabled, "aria-label": this.ariaLabel }, h("span", { key: '2924ef427fb12f70f2245561ca0c44087841a4a6', class: "mdc-button__ripple" }), h("span", { key: 'ec9fdf34ba61179fe2e1576f9daaf145ef4772c6', class: "mdc-button__focus-ring" }), this.icon &&
            h("vx-icon", { size: this.isQuadratic ? '20px' : '24px', icon: this.icon, customClass: "mdc-button__icon" }), h("span", { key: 'd58902128020f892b3ceae8738c5d899faeabe07', class: "mdc-button__label" }, (_a = this.label) !== null && _a !== void 0 ? _a : h("slot", null)))));
    }
    static get is() { return "vx-button"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["vx-button.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["vx-button.css"]
        };
    }
    static get properties() {
        return {
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Whether the button is disabled."
                },
                "attribute": "disabled",
                "reflect": true,
                "defaultValue": "false"
            },
            "ariaLabel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The aria-label attribute of the button."
                },
                "attribute": "aria-label",
                "reflect": true
            },
            "raised": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Whether the button is raised."
                },
                "attribute": "raised",
                "reflect": false,
                "defaultValue": "false"
            },
            "fullwidth": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Whether the button is full width."
                },
                "attribute": "fullwidth",
                "reflect": false,
                "defaultValue": "false"
            },
            "icon": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The icon of the button."
                },
                "attribute": "icon",
                "reflect": false
            },
            "showLoadingIndicator": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Whether to show a loading indicator."
                },
                "attribute": "show-loading-indicator",
                "reflect": false,
                "defaultValue": "false"
            },
            "unelevated": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Whether the button is unelevated."
                },
                "attribute": "unelevated",
                "reflect": false,
                "defaultValue": "false"
            },
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The label of the button."
                },
                "attribute": "label",
                "reflect": false
            },
            "success": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Whether the button is in a success state."
                },
                "attribute": "success",
                "reflect": false,
                "defaultValue": "false"
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "'text' | 'email' | 'number' | 'submit' | string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The type of the button."
                },
                "attribute": "type",
                "reflect": true
            },
            "background": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The background color of the button."
                },
                "attribute": "background",
                "reflect": false
            },
            "isQuadratic": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Whether the button is quadratic."
                },
                "attribute": "is-quadratic",
                "reflect": false,
                "defaultValue": "false"
            },
            "height": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The height of the button."
                },
                "attribute": "height",
                "reflect": false,
                "defaultValue": "'36px'"
            },
            "width": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The width of the button."
                },
                "attribute": "width",
                "reflect": false
            }
        };
    }
    static get elementRef() { return "host"; }
    static get watchers() {
        return [{
                "propName": "success",
                "methodName": "handleSuccess"
            }];
    }
}
//# sourceMappingURL=vx-button.js.map
